import React, { useEffect, useRef, MutableRefObject, useCallback } from 'react'
import { DropdownItem, HelpTip } from '@amzn/storm-ui'
import {
  FeedbackForm as StormFeedbackForm,
  SentimentScaleForFeedbackFormProps,
  SentimentScaleForFeedbackForm,
  TextAreaForFeedbackForm,
  FeedbackFormProps as StormFeedbackFormProps,
  DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE,
  loadTranslations,
} from '@amzn/storm-ui-feedback-form-kit'
import {
  AAC_FIT_FEEDBACK_TRIGGER,
  AAC_FIT_FEEDBACK_TRIGGER_CLOSE,
  AAC_FIT_FEEDBACK_TRIGGER_STATE_UPDATE,
  AAC_FIT_FEEDBACK_TRIGGER_SUBMIT,
  DismissReason,
  getFeedbackId,
  useFeedbackFormId,
  GetFeedbackTriggerEventDetail,
  FeedbackFormSubmitEventDetail,
  FeedbackFormCloseEventDetail,
  FeedbackFormUpdateStateEventDetail,
} from '@amzn/intro-widgets-api'
import { handleRequest, METHOD } from '../../utils/handleRequest'
import { FeedbackModalMetricsPublisher, FeedbackModalAjaxCallMetricsPublisher } from '../../utils/metrics'
import { logger } from '../../utils/logger'
import { logWithTakt } from '../../utils/takt'
import { ErrorBoundary } from '../ErrorBoundary'
import { handleSubmit } from './handleSubmit'
import DropdownForFeedbackForm from './DropdownForFeedbackForm'
import useFeedbackState from './useFeedbackState'
import getStormLocale from './getStormLocale'
import { getDebugConfig } from './debugger'
import type { EventData, FormMetadata, FormOnValidate, SubmissionData, VocApiPayload } from './Types'

const mapRatingTypeToVariant = (ratingType: string, formVariant?: SentimentScaleForFeedbackFormProps['variant']) => {
  const ratingTypeMap: Record<string, SentimentScaleForFeedbackFormProps['variant']> = {
    EMOJI: 'emoji',
    NPS_10: 'numeric-10',
  }
  return ratingTypeMap[ratingType] || formVariant || 'emoji'
}

const ajaxEndpoint = '/ccx/ajax/cssf'
export const ajaxRequestTimeout = 3000
// Setting portalElementId to undefined as the form is already rendered in AACChromePortal
const theme = { popper: { portalElementId: undefined } }

const findFormMetadata = (namespaceMetadata: FormMetadata[], eventDetail: EventData) =>
  namespaceMetadata.find((metaData) => {
    if (!metaData) {
      return false
    }
    const { applicationName, pageName, componentName } = metaData
    // some pages have no pageId
    // https://code.amazon.com/packages/AACChromeUI/blobs/cc7b4ee19f740911a64f0dc20231d37a244a2461/--/src/features/footer/components/FeedbackTrigger.tsx#L29
    if (eventDetail?.page === 'Unknown') {
      return eventDetail?.app === applicationName && eventDetail?.component === componentName
    }
    return eventDetail?.app === applicationName && eventDetail?.page === pageName && eventDetail?.component === componentName
  })

const validate = async (values: Record<string, unknown>, onValidate?: FormOnValidate) => {
  try {
    const result = {
      values,
      errors: {},
    }

    if (onValidate) {
      const { values: validatedValues, errors: validatedErrors } = await onValidate(values)
      result.values = validatedValues ?? values
      result.errors = validatedErrors ?? {}
    }

    // At least one question needs to be answered
    if (Object.keys(result.errors).length === 0) {
      const valuesEntries = Object.entries(values)
      const noQuestionsAnswered = valuesEntries.every(([, value]) => value === null || typeof value === 'undefined' || (typeof value === 'string' && value === ''))
      if (noQuestionsAnswered) {
        result.errors[valuesEntries[0][0]] = { message: DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE }
      }
    }

    return result
  } catch (error) {
    logger.error(`Failed to validate feedback form, error: ${error instanceof Error ? error.message : error}`)
    return {
      values,
      errors: {
        [Object.keys(values)[0]]: { message: DEFAULT_FEEDBACK_FORM_ERROR_MESSAGE },
      },
    }
  }
}

const getHandleValidate = (onValidate?: FormOnValidate) => async (values) => {
  const result = await validate(values, onValidate)
  return result
}

async function onSubmit(
  data: Record<string, unknown>,
  eventDetail: EventData,
  feedbackModalData: FormMetadata,
  submissionDataRef: MutableRefObject<SubmissionData | undefined>,
  locale: string,
  vocApiPayload: VocApiPayload,
  callback: (error?: Error) => void
) {
  try {
    const simulateVocApiCall = getDebugConfig({ application: eventDetail.app, page: eventDetail.page, component: eventDetail.component })?.simulateVocApiCall
    if (!simulateVocApiCall || !['failure', 'success'].includes(simulateVocApiCall)) {
      if (!submissionDataRef.current) {
        const INTERVAL = 10
        for (let i = 0; i < ajaxRequestTimeout; i += INTERVAL) {
          if (submissionDataRef.current) {
            break
          }
          await new Promise((resolve) => setTimeout(resolve, INTERVAL))
        }

        if (!submissionDataRef.current) {
          throw new Error('Ajax API response not available')
        }
      }
    }

    await handleSubmit(data, eventDetail, feedbackModalData, submissionDataRef.current, locale, vocApiPayload)
    callback()
  } catch (error: unknown) {
    logger.error(`Failed to submit feedback form, error: ${error instanceof Error ? error.message : error}`)
    if (error instanceof Error) {
      callback(error)
    }
    throw error
  }
}

const fetchSubmissionData = async (event, onSuccess, onError) => {
  try {
    const logMessage = `asyncRequests - fetch feedback modal metadata for event: app=${event?.app}&page=${event?.page}&component=${event?.component}`
    const url = `${ajaxEndpoint}?app=${event?.app}&page=${event?.page}&component=${event?.component}`
    const result = await handleRequest(
      {
        url: url,
        method: METHOD.GET,
        timeout: ajaxRequestTimeout,
      },
      logMessage,
      FeedbackModalAjaxCallMetricsPublisher
    )
    if (result && result.status === 200 && result.data) {
      onSuccess(result)
    }
  } catch (error) {
    onError(error)
  }
}

const updateFormState = (detail: FeedbackFormUpdateStateEventDetail) => {
  if (document) {
    document.dispatchEvent(
      new CustomEvent(AAC_FIT_FEEDBACK_TRIGGER_STATE_UPDATE, {
        bubbles: true,
        detail,
      })
    )
  }
}

export interface FeedbackFormProps {
  namespaceMetadata: FormMetadata[]
  isMobile: boolean
  locale: string
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ namespaceMetadata, isMobile, locale }) => {
  const anchorRef = useRef()
  const currentLocaleRef = useRef<string | undefined>()
  const [
    {
      isOpen,
      eventDetail,
      formMetadata,
      formkey,
      formType,
      formHideActions,
      formPopperOffset,
      formPopperPlacement,
      formOnDismiss,
      formOnValidate,
      withoutConfirmation,
      advertiserInfo,
      feedbackContext,
      submissionInfo,
    },
    dispatch,
  ] = useFeedbackState()
  const submissionDataRef = useRef<SubmissionData>()
  const dismissReasonRef = useRef<Exclude<DismissReason, 'ineligible'>>('canceled')
  const translationMapRef = useRef<{ locale?: string; translationMap?: undefined | Record<string, string> }>({})
  const stormFeedbackFormRef = useRef<null | { reset: () => void; submit: () => void }>(null)
  const feedbackId = eventDetail ? getFeedbackId({ ...(eventDetail ?? {}), type: formType }) : undefined
  const submitErrorMessageRef = useRef<undefined | string>(undefined)

  const close = useCallback(
    (reason?: DismissReason) => {
      dispatch({ type: 'close' })
      if (reason) {
        formOnDismiss?.(reason)
      }
      // Reset formState
      submitErrorMessageRef.current = undefined
      if (feedbackId) {
        updateFormState({
          id: feedbackId,
          isDirty: false,
          isValid: false,
          isValidating: false,
          isSubmitEnabled: false,
          isSubmitSuccessful: false,
          isSubmitting: false,
          submitErrorMessage: submitErrorMessageRef.current,
        })
      }
    },
    [dispatch, formOnDismiss, feedbackId]
  )

  const handleFormStateChange = useCallback(
    ({
      isDirty,
      isValid,
      isValidating,
      isSubmitEnabled,
      isSubmitSuccessful,
      isSubmitting,
    }: {
      isDirty: boolean
      isValid: boolean
      isValidating: boolean
      isSubmitEnabled: boolean
      isSubmitSuccessful: boolean
      isSubmitting: boolean
      validationErrorMessage: string | undefined
    }) => {
      if (isSubmitting) {
        submitErrorMessageRef.current = undefined
      }
      if (feedbackId) {
        updateFormState({
          id: feedbackId,
          isDirty,
          isValid,
          isValidating,
          isSubmitEnabled,
          isSubmitSuccessful,
          isSubmitting,
          submitErrorMessage: submitErrorMessageRef.current,
        })
      }

      if (isSubmitSuccessful) {
        if (withoutConfirmation && formType !== 'inline') {
          close(dismissReasonRef.current)
        }
      }
    },
    [close, feedbackId, formType, withoutConfirmation]
  )

  // Prefetch translations
  useEffect(() => {
    const fetchTranslations = async (originlocale: string) => {
      try {
        if (translationMapRef.current?.locale !== originlocale) {
          const stormLocale = getStormLocale(originlocale)
          if (stormLocale) {
            const getTranslationsModule = (loadTranslations(stormLocale) as { translationImports?: Record<string, () => Promise<{ translations?: Record<string, string> }>> })?.translationImports?.[
              stormLocale
            ]
            if (getTranslationsModule) {
              const translationsModule = await getTranslationsModule()
              if (translationsModule?.translations) {
                translationMapRef.current = {
                  locale: originlocale,
                  translationMap: translationsModule.translations,
                }
              }
            }
          }
        }
      } catch (error) {
        // @TODO: add logger
      }
    }

    fetchTranslations(locale)
  }, [locale])

  // Handle submit event
  useEffect(() => {
    const handleEvent = (e) => {
      const detail: FeedbackFormSubmitEventDetail = e.detail
      if (detail.id === feedbackId) {
        stormFeedbackFormRef.current?.submit?.()
      }
    }

    if (document) {
      document.addEventListener(AAC_FIT_FEEDBACK_TRIGGER_SUBMIT, handleEvent)
    }

    return () => {
      if (document) {
        document.removeEventListener(AAC_FIT_FEEDBACK_TRIGGER_SUBMIT, handleEvent)
      }
    }
  }, [feedbackId])

  // Handle close event
  useEffect(() => {
    const handleEvent = (e) => {
      const detail: FeedbackFormCloseEventDetail = e.detail
      if (detail.id === feedbackId) {
        close(detail.fromExternal ? dismissReasonRef.current : undefined)
      }
    }

    document?.addEventListener(AAC_FIT_FEEDBACK_TRIGGER_CLOSE, handleEvent)

    return () => {
      document?.removeEventListener(AAC_FIT_FEEDBACK_TRIGGER_CLOSE, handleEvent)
    }
  }, [close, feedbackId])

  // Handle trigger event
  useEffect(() => {
    const handleEvent = (e) => {
      if (feedbackId === getFeedbackId({ ...(e?.detail ?? {}) })) {
        return
      }

      const newFormMetadata = findFormMetadata(namespaceMetadata, e?.detail)
      if (!isOpen && newFormMetadata) {
        FeedbackModalMetricsPublisher.publishCounterMonitor(`triggerClicked-${e.detail.app}-${e.detail.page}-${e.detail.component}`, 1)
        const detail: GetFeedbackTriggerEventDetail = e.detail
        anchorRef.current = detail?.anchorRef ? detail?.anchorRef.current : e.target
        if (!submissionDataRef.current) {
          fetchSubmissionData(
            detail,
            (result) => {
              submissionDataRef.current = result.data.submissionData
            },
            (error) => {
              logger.error(`Failed to fetch feedback form data from AJAX endpoint, error: ${error instanceof Error ? error.message : error}`)
            }
          )
        }
        dismissReasonRef.current = 'canceled'
        dispatch({
          type: 'open',
          eventDetail: detail,
          formMetadata: newFormMetadata,
          formType: detail.type,
          formHideActions: detail.hideActions,
          formPopperOffset: detail.popperOffset,
          formPopperPlacement: detail.popperPlacement,
          formOnDismiss: detail.onDismiss,
          formOnValidate: detail.onValidate,
          withoutConfirmation: detail.afterSubmission === 'dismissWithoutConfirmation',
          advertiserInfo: detail.advertiserInfo,
          feedbackContext: detail.feedbackContext,
          submissionInfo: detail.submissionInfo,
        })
      } else {
        const detail = e.detail
        detail?.onDismiss?.('ineligible')
      }
    }

    document?.addEventListener(AAC_FIT_FEEDBACK_TRIGGER, handleEvent)

    return () => {
      document?.removeEventListener(AAC_FIT_FEEDBACK_TRIGGER, handleEvent)
    }
  }, [dispatch, namespaceMetadata, isOpen, feedbackId])

  const { setFeedbackFormId } = useFeedbackFormId()

  // Update feedbackId
  useEffect(() => {
    if (isOpen) {
      setFeedbackFormId(feedbackId)
    } else {
      setFeedbackFormId(undefined)
    }
  }, [feedbackId, isOpen, setFeedbackFormId])

  // Update feedbackId
  useEffect(() => {
    // set id to undefined if unmounted
    return () => {
      setFeedbackFormId(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!eventDetail || !formMetadata) {
    return null
  }

  const taktId = feedbackId
  const { anchorId, headerText, title, submitSuccessMessage, submitFailMessage, additionalTextLabel, additionalHelpTipLabel, submitLabel, cancelLabel, rating, inputTextArea, dropdown } = formMetadata

  return (
    <ErrorBoundary anchorId={anchorId} mobileView={isMobile} customMetricsPublisher={FeedbackModalMetricsPublisher}>
      <StormFeedbackForm
        withoutConfirmation={withoutConfirmation}
        ref={stormFeedbackFormRef}
        translationMap={translationMapRef.current.locale === locale ? translationMapRef.current.translationMap : undefined}
        onFormStateChange={handleFormStateChange}
        onLoadSucceeded={() => {
          if (currentLocaleRef.current !== locale) {
            currentLocaleRef.current = locale
            logWithTakt(`cssf-feedback-loaded-${eventDetail?.app}-${eventDetail?.page}-${eventDetail?.component}`, {})
            FeedbackModalMetricsPublisher.publishCounterMonitor(`rendered-${eventDetail?.app}-${eventDetail?.page}-${eventDetail?.component}`, 1)
          }
        }}
        onLoadFailed={(error) => {
          logger.error(`Failed to load translation for feedback form, error: ${error instanceof Error ? error.message : error}, locale: ${locale}`)
          close('ineligible')
          currentLocaleRef.current = undefined
        }}
        key={formkey}
        type={formType}
        // @TODO: update once hideActions is available
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        hideActions={formHideActions}
        popperOffset={formPopperOffset}
        popperPlacement={formPopperPlacement as StormFeedbackFormProps['popperPlacement']}
        taktId={taktId}
        id={`cssf-feedback-modal-${eventDetail?.app}-${eventDetail?.page}-${eventDetail?.component}`}
        renderHeader={headerText}
        label={headerText}
        locale={getStormLocale(locale)}
        onSubmit={(data) =>
          onSubmit(data, eventDetail, formMetadata, submissionDataRef, locale, { advertiserInfo, feedbackContext, submissionInfo }, (error) => {
            if (error === undefined) {
              dismissReasonRef.current = 'success'
            } else {
              dismissReasonRef.current = 'failure'
              submitErrorMessageRef.current = error.message
            }
          })
        }
        onValidate={getHandleValidate(formOnValidate)}
        isOpen={isOpen}
        anchorElement={anchorRef}
        onClose={() => {
          close(dismissReasonRef.current)
        }}
        isMobile={isMobile}
        renderPrompt={title}
        renderSuccess={submitSuccessMessage}
        renderError={submitFailMessage}
        renderDisclaimer={() => (
          <>
            {additionalTextLabel}
            <HelpTip nonInteractive>{additionalHelpTipLabel}</HelpTip>
          </>
        )}
        // optional fields, storm component has default value for these fields
        // we are using the ones passed from backend
        submitButtonLabel={submitLabel}
        cancelButtonLabel={cancelLabel}
        theme={theme}
      >
        {rating && (
          <SentimentScaleForFeedbackForm
            taktId={`${taktId}-rating`}
            id="cssf-sentiment-scale"
            name="rating"
            label={rating.ratingTitle}
            variant={mapRatingTypeToVariant(rating.ratingType)}
            options={
              rating.ratingDescription
                ? Object.entries(rating.ratingDescription)
                    .sort(([valueA], [valueB]) => Number(valueA) - Number(valueB))
                    .map(([id, label]) => ({
                      id,
                      label,
                    }))
                : undefined
            }
            minLabel={rating.ratingLowRatingLabel}
            maxLabel={rating.ratingHighRatingLabel}
          />
        )}
        {dropdown && (
          <DropdownForFeedbackForm
            taktId={`${taktId}-dropdown`}
            id="cssf-dropdown"
            name="dropdown"
            label={dropdown.dropdownTitle}
            secondaryViewCloseButtonLabel={dropdown.secondaryViewCloseButtonLabel}
            defaultLabel={dropdown.dropdownDefaultLabel}
          >
            {dropdown.dropdownItems.map(({ itemKey, itemValue }) => (
              <DropdownItem key={itemKey} value={itemValue}>
                {itemValue}
              </DropdownItem>
            ))}
          </DropdownForFeedbackForm>
        )}
        {inputTextArea && (
          <TextAreaForFeedbackForm
            taktId={`${taktId}-text`}
            id="cssf-textarea"
            name="inputTextArea"
            label={inputTextArea.label}
            placeholder={inputTextArea.placeholderText}
            maxGlyphCount={inputTextArea.characterLimit}
          />
        )}
      </StormFeedbackForm>
    </ErrorBoundary>
  )
}

export default FeedbackForm
